<template>
	<div ref="triggerWrapper">
		<PopoverRoot v-model:open="isOpen">
			<PopoverAnchor>
				<slot name="trigger" :isOpen="isOpen" :toggle="toggle" :addAndOpen="addAndOpen">
					<PopoverTrigger as-child>
						<button
							type="button"
							class="btn btn-sm btn-ghost btn-circle bg-base-100"
						>
							<ListBulletIcon class="w-4 h-4"/>
						</button>
					</PopoverTrigger>
				</slot>
			</PopoverAnchor>
			<PopoverPortal>
				<PopoverContent
					:side="side"
					:side-offset="5"
					:align="align"
					:align-offset="-16"
					:collision-padding="16"
					:avoid-collisions="true"
					sticky="always"
					class="bg-white rounded-lg shadow-xl border w-96 sm:max-w-md flex flex-col p-4 max-h-[min(66vh,var(--reka-popover-content-available-height))] z-50"
				>
					<!-- Header -->
					<div class="flex justify-between items-center mb-4 flex-shrink-0">
						<div class="font-semibold text-lg">{{ computedTitle }}</div>
						<!-- <PopoverClose
							class="btn btn-sm btn-ghost btn-circle"
							aria-label="Close"
						>
							<XMarkIcon class="cursor-pointer w-5 h-5" />
						</PopoverClose> -->
					</div>

					<!-- Main content area -->
					<div class="relative flex-1 min-h-0 flex flex-col overflow-x-hidden">
						<!-- Agenda context -->
						<!-- <div v-if="(context === 'agenda' || context === 'agenda-overview') && !isSavedToLibrary" class="flex-shrink-0"> -->
						<!-- Opening the popover adds the plan to my library: only shown while that's being saved -->
						<div v-if="!lessonPlan.isInMyLibrary" class="flex justify-center py-6 flex-shrink-0">
							<span class="loading loading-spinner loading-md"></span>
						</div>

						<!-- Collections section -->
						<template v-else>

							<!-- What My Library is good for, right after adding the plan to it -->
							<div
								v-if="justAddedToLibrary"
								class="text-sm flex items-start gap-2 p-2 mb-2 bg-green-100 text-success-content rounded flex-shrink-0"
								data-testid="added-to-library-info"
							>
								<CheckCircleIcon class="w-5 h-5 shrink-0" />
								<p>{{ $t("#info: lesson plan added to library") }}</p>
							</div>

							<div class="flex-1 min-h-40 overflow-y-auto overflow-x-hidden pr-2 pb-12" @scroll.stop>
								<!-- Create + Sort toolbar -->
								<div class="sticky top-0 z-10 pb-4 pt-2 flex-shrink-0 bg-white/70 backdrop-blur-sm">
									<div class="flex justify-between items-center pr-2" :class="{ 'opacity-0 pointer-events-none': showCreateInput }">
										<SortableHeader property="name" :order="localOrder" @change="handleOrderChange" />
										<button
											type="button"
											class="btn btn-primary btn-xs btn-circle"
											@click="onCreateClick"
										>
											<PlusIcon class="w-4 h-4" />
										</button>
									</div>
									<Transition name="slide-fade">
										<LessonPlanCollectionEditor
											v-if="showCreateInput"
											class="absolute inset-x-0 top-0 z-10"
											@requestUnmount="showCreateInput = false"
											@created="onCollectionCreated"
										/>
									</Transition>
								</div>

								<!-- Empty state -->
								<div v-if="!isLoading && !displayCollections.length" class="text-center text-base-content-light italic text-sm mb-12">{{ $t('No collections created yet') }}</div>

								<!-- Loading state -->
								<div v-else-if="isLoading" class="flex justify-center py-4" :class="{ 'opacity-40 pointer-events-none': showCreateInput }">
									<Skeleton content-type="userlist" :items="5" :withThumb="false" :context="'menu'" />
								</div>

								<!-- Collection items -->
								<ul v-else class="flex flex-col gap-2" :class="{ 'opacity-40 pointer-events-none': showCreateInput }">
									<li v-for="(item, index) in displayCollections" :key="item.id">
										<LessonPlanCollectionsListItem
											:collection="item"
											:showMarker="true"
											:showCheckbox="true"
											:checked="isSelected(item)"
											:loading="savingCollectionIds.has(item.id)"
											@click="toggleCollection(item)"
										/>
										<div
											v-if="shouldShowDividerAfter(index)"
											class="my-2 border-b border-base-100"
										></div>
									</li>
								</ul>
							</div>
						</template>
					</div>

					<!-- Footer -->
					<div class="absolute bottom-0 left-0 z-10 w-full flex items-center justify-between gap-2 rounded-b-lg bg-white/70 backdrop-blur-sm border border-base-200 shrink-0">

						<div v-if="confirmingRemove" class="flex flex-col flex-1 items-center gap-4 p-2 bg-base rounded-b-lg shadow-[0_-8px_16px_-6px_rgba(0,0,0,0.15)]">
							<p class="text-sm font-semibold">{{ $t('Are you sure?') }}</p>
							<div class="flex gap-2">
								<button type="button" class="btn btn-sm btn-ghost" data-testid="cancel-remove-from-library" :disabled="removingFromLibrary" @click="confirmingRemove = false">
									{{ $t("Cancel") }}
								</button>
								<button type="button" class="btn btn-sm btn-danger" data-testid="confirm-remove-from-library" :disabled="removingFromLibrary" @click="removeFromLibrary">
									<span v-show="removingFromLibrary" class="loading loading-spinner loading-xs"></span>
									{{ $t("Remove") }}
								</button>
							</div>
							<!-- Consequences if you own the lesson plan -->
							<p v-if="isOwnedByMe" class="p-2 text-sm bg-red-200 text-error-content rounded whitespace-pre-line" data-testid="remove-warning-owner">
								{{  $t('#notification: deleting your own lesson plan from your library')  }}
							</p>
							<!-- Consequences if not owning the lesson plan -->
							<p v-else class="text-sm text-base-content-light" data-testid="remove-warning-other">
								{{ $t('#notification: deleting your non-own lesson plan from your library') }}
							</p>
						</div>

						<template v-else>
							<!-- For any lesson plan in my library; the confirmation says what it means for the owner -->
							<button
								v-if="canRemoveFromLibrary"
								type="button"
								class="btn btn-ghost text-danger ml-1"
								data-testid="remove-from-library"
								@click="confirmingRemove = true"
							>
								{{ $t("Remove from My Library") }}
							</button>
							<PopoverClose
								class="flex items-center p-1 justify-end mr-2 ml-auto"
								aria-label="Close"
							>
								<button type="button" class="btn btn-ghost">
									{{ $t("Done") }}
								</button>
							</PopoverClose>
						</template>
					</div>
				</PopoverContent>
			</PopoverPortal>
		</PopoverRoot>
	</div>
</template>

<script lang="ts">
import { XMarkIcon, PlusIcon, CheckCircleIcon, ListBulletIcon } from "@heroicons/vue/24/outline";
import { mapStores } from "pinia";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useUsersStore } from "@/stores/Users.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import LessonPlanCollectionsListItem from "@/components/LessonPlanCollectionsListItem.vue";
import SortableHeader from "@/components/ui/SortableHeader.vue";
import Skeleton from "@/components/ui/Skeleton.vue";
import LessonPlanCollectionEditor from "@/components/LessonPlanCollectionEditor.vue";
import { PopoverArrow, PopoverClose, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger, PopoverAnchor } from 'reka-ui'
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import { OrderDirection, OrderParameter } from "@/models/OrderParameter.model";

export default {
	name: "LessonPlanCollectionsSelector",

	emits: ["collectionsChanged", "addedToLibrary", "removedFromLibrary"],

	props: {
		lessonPlan: {
			type: Object as any,
			required: true,
		},
		// context: {
		// 	type: String,
		// 	default: "library", // 'library','agenda', 'discover' and 'agenda-library'
		// 	// there is also 'agenda-overview' to handle the case when the selector
		// 	// is used in the agenda itself and not in the drawer in agenda context
		// },
		title: {
			type: String,
		},
		align: {
			type: String,
			default: "end",
			validator: (value: string) => ["start", "center", "end"].includes(value),
		},
		side: {
			type: String,
			default: "bottom",
			validator: (value: string) => ["top", "bottom", "left", "right"].includes(value),
		},
	},

	components: {
		XMarkIcon,
		PlusIcon,
		CheckCircleIcon,
		ListBulletIcon,
		LessonPlanCollectionsListItem,
		SortableHeader,
		Skeleton,
		LessonPlanCollectionEditor,
		PopoverArrow,
		PopoverClose,
		PopoverContent,
		PopoverPortal,
		PopoverRoot,
		PopoverTrigger,
		PopoverAnchor,
	},

	data() {
		return {
			isOpen: false,
			isLoading: false,
			showCreateInput: false,
			savingCollectionIds: new Set<string>(),
			savingToLibrary: false,
			justAddedToLibrary: false,
			confirmingRemove: false,
			removingFromLibrary: false,
			snapshotCollections: null as any[] | null,
			localOrder: new OrderParameter('name', OrderDirection.ASC),
		};
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useLessonPlansStore, useUsersStore),

		canRemoveFromLibrary(): boolean {
			return !!this.lessonPlan?.isInMyLibrary && !!this.usersStore.me;
		},

		// Removing is the same for everyone (out of my own library and collections), but for the owner it
		// also means the plan is no longer shared, which the warning says.
		isOwnedByMe(): boolean {
			const me = this.usersStore.me;
			return !!me && this.lessonPlan.isOwnedBy(me);
		},

		computedTitle(): string {
			return this.title || this.$t("Add to library collections");
		},

		/**
		 * Store items partitioned into selected-first, then unselected.
		 * Sorting within each group is handled by the store.
		 */
		displayCollections(): any[] {
			const collections = this.snapshotCollections ?? this.lessonPlanCollectionsStore.getItems(false, this.localOrder);
			return collections.filter((l: any) => l?.id && l?.name);
		},
	},

	watch: {
		isOpen(newVal: boolean) {
			if (newVal) {
				this.initialize();
			} else {
				this.justAddedToLibrary = false;
				this.confirmingRemove = false;
			}
		},
	},

	methods: {
		// Opening it for a lesson plan that isn't in my library adds it: there is no in-between step.
		toggle() {
			if (this.isOpen) {
				this.isOpen = false;
				return;
			}
			this.addAndOpen();
		},

		open() {
			this.isOpen = true;
			this.snapshotCollections = null;
			this.initialize();
		},

		// One click adds the plan to my library if it isn't in it yet, then shows the collections.
		async addAndOpen() {
			this.isOpen = true;
			if (!this.lessonPlan?.isInMyLibrary) {
				const added = await this.saveToLibrary();
				// Not added: nothing to show (the error is notified)
				if (!added) this.isOpen = false;
			}
		},

		async initialize() {
			this.isLoading = true;
			try {
				await this.lessonPlanCollectionsStore.loadOnce();

				// Partition once at open time, then freeze
				const collections = this.lessonPlanCollectionsStore.getItems(false, this.localOrder);

				const selected = collections
					.filter((l: LessonPlanCollection) => l?.id && l?.name && this.isSelected(l))
					.map((l: LessonPlanCollection) => {
						l._selected = true; // add a temporary property to keep track of selection in the UI
						return l;
					});

				const unselected = collections
					.filter((l: LessonPlanCollection) => l?.id && l?.name && !this.isSelected(l))
					.map((l: LessonPlanCollection) => {
						l._selected = false;
						return l;
					});

				this.snapshotCollections = [...selected, ...unselected];
			} catch (e) {
				console.warn("Failed to load lesson plan collections:", e);
			}
			this.isLoading = false;
		},

		isSelected(item: any): boolean {
			return (this.lessonPlan?.collectionIds ?? []).includes(item.id);
		},

		shouldShowDividerAfter(index: number): boolean {
			const collections = this.snapshotCollections ?? [];
			const current = collections[index];
			const next = collections[index + 1];
			return !!current?._selected && !!next && !next._selected;
		},

		async toggleCollection(item: any) {
			if (!this.lessonPlan?.id || !item?.id) return;

			const listId = item.id;
			const isCurrentlySelected = this.isSelected(item);

			// Mark as saving (force reactivity via reassignment)
			this.savingCollectionIds.add(listId);
			this.savingCollectionIds = new Set(this.savingCollectionIds);

			try {
				if (isCurrentlySelected) {
					await this.lessonPlanCollectionsStore.removeLessonPlanFromCollection(item, this.lessonPlan);
				} else {
					await this.lessonPlanCollectionsStore.addLessonPlanToCollection(item, this.lessonPlan);
				}
				// check the current filter of the LessonPlansStore if the collection being removed/added is currently filtered on,
				// if so reload the lesson plans to reflect the change, otherwise we would still see the removed lesson plan in the list until next reload
				const currentLessonPlanFilter = this.lessonPlansStore.getCurrentFilter();
				if (currentLessonPlanFilter.collectionIds?.includes(item.id)) {
					await this.lessonPlansStore.load();
				}
				this.$emit("collectionsChanged", this.lessonPlan.collectionIds);
			} catch (e: any) {

				if (e instanceof ApiErrorMessageBag) {
					this.$notify({
						text: e.toString() || this.$t("An error occurred while updating the collection"),
						type: "error",
					});
				} else {
					console.error(e);
					this.$notify({
						text: this.$t("An error occurred while updating the collection"),
						type: "error",
					});
				}

			} finally {
				this.savingCollectionIds.delete(listId);
				this.savingCollectionIds = new Set(this.savingCollectionIds);
			}
		},

		async saveToLibrary(): Promise<boolean> {
			if (!this.lessonPlan) return false;

			this.savingToLibrary = true;

			try {
				// @todo check if this is correct
				// scenarios:
				// 1. this is a brand new lesson plan -> save!
				// 2. this is a lesson plan owned by someone else -> addToLibrary
				// 3. this is a lesson plan that is attached to a lesson item, but is not in a library -> save
				// 4. more???
				if (this.lessonPlan.id) {
					await this.lessonPlansStore.addToLibrary(this.lessonPlan);
				} else {
					await this.lessonPlansStore.save(this.lessonPlan);
				}

				this.lessonPlan.isInMyLibrary = true;
				this.justAddedToLibrary = true;
				this.$emit("addedToLibrary", this.lessonPlan);
				return true;
			} catch (e: any) {
				if (e instanceof ApiErrorMessageBag) {
					this.$notify({
						text: e.toString() || this.$t("Failed to save to library"),
						type: "error",
					});
				} else {
					console.error(e);
					this.$notify({
						text: this.$t("Failed to save to library"),
						type: "error",
					});
				}
				return false;
			} finally {
				this.savingToLibrary = false;
			}
		},

		async removeFromLibrary() {
			if (this.removingFromLibrary) return;
			this.removingFromLibrary = true;

			try {
				// Out of your collections first: they would otherwise keep pointing at it
				await Promise.all((this.lessonPlan.collectionIds ?? []).map(async (collectionId: string) => {
					const collection = this.lessonPlanCollectionsStore.find(collectionId) as LessonPlanCollection;
					if (!collection) return;
					try {
						await this.lessonPlanCollectionsStore.removeLessonPlanFromCollection(collection, this.lessonPlan);
					} catch (err) {
						console.warn("Failed removing lesson plan from collection", collectionId, err);
					}
				}));

				await this.lessonPlansStore.delete(this.lessonPlan);

				this.lessonPlan.isInMyLibrary = false;
				this.lessonPlan.collectionIds = [];
				this.$notify({ text: this.$t("Lesson plan removed from library"), type: "success" });
				this.$emit("removedFromLibrary", this.lessonPlan);
				this.isOpen = false;
			} catch (e: any) {
				if (!(e instanceof ApiErrorMessageBag)) console.error(e);
				this.$notify({
					text: (e instanceof ApiErrorMessageBag && e.toString()) || this.$t("An error occurred"),
					type: "error",
				});
			} finally {
				this.removingFromLibrary = false;
				this.confirmingRemove = false;
			}
		},

		onCreateClick() {
			this.showCreateInput = true;
		},

		async onCollectionCreated(payload: { collection: LessonPlanCollection, context: any }) {
			this.showCreateInput = false;

			const createdCollection = payload?.collection;
			if (!createdCollection?.id || !this.lessonPlan?.id) return;
			if (!this.snapshotCollections || this.snapshotCollections.find((c: any) => c.id === createdCollection.id)) return;

			const items = this.lessonPlanCollectionsStore.getItems(false, this.localOrder);
			const selected = items.filter((l: any) => this.isSelected(l) || l.id === createdCollection.id).map((l: any) => ({ ...l, _selected: true }));
			const unselected = items.filter((l: any) => !this.isSelected(l) && l.id !== createdCollection.id).map((l: any) => ({ ...l, _selected: false }));
			this.snapshotCollections = [...selected, ...unselected];

			await this.toggleCollection(createdCollection);
		},

		handleOrderChange(newOrder: OrderParameter) {
			this.localOrder = newOrder;

			if (!this.snapshotCollections) return;

			const selectedIds = new Set(
				this.snapshotCollections
					.filter((c: any) => c._selected)
					.map((c: any) => c.id)
			);

			const sortedItems = this.lessonPlanCollectionsStore.getItems(false, newOrder);
			const selected = sortedItems.filter((c: any) => selectedIds.has(c.id));
			const unselected = sortedItems.filter((c: any) => !selectedIds.has(c.id));

			this.snapshotCollections = [...selected, ...unselected];
		},
	},
};
</script>
